import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { getPreference, upsertPreference } from '@/services/preferences'

const THEME_KEY = 'theme_preference'

export const useThemeStore = defineStore('theme', () => {
  const saved = (localStorage.getItem(THEME_KEY) as 'light' | 'dark' | 'system') || 'system'
  const theme = ref<'light' | 'dark' | 'system'>(saved)

  const systemDark = ref(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false,
  )

  const isDark = computed(() => {
    if (theme.value === 'system') return systemDark.value
    return theme.value === 'dark'
  })

  function applyTheme(dark: boolean) {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light')
  }

  async function setTheme(value: 'light' | 'dark' | 'system') {
    theme.value = value
    localStorage.setItem(THEME_KEY, value)
    applyTheme(isDark.value)

    const rawUser = localStorage.getItem('user')
    if (rawUser) {
      try {
        const user = JSON.parse(rawUser)
        if (user?.id) {
          await upsertPreference(user.id, { app_theme: value }).catch(() => {})
        }
      } catch {
        // ignore
      }
    }
  }

  async function syncFromDb(userId: string) {
    try {
      const pref = await getPreference(userId)
      if (pref?.app_theme) {
        theme.value = pref.app_theme
        localStorage.setItem(THEME_KEY, pref.app_theme)
        applyTheme(isDark.value)
      }
    } catch {
      // fallback to localStorage value
    }
  }

  function init() {
    applyTheme(isDark.value)
    if (typeof window !== 'undefined') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)')
      const handler = (e: MediaQueryListEvent) => {
        systemDark.value = e.matches
        if (theme.value === 'system') applyTheme(e.matches)
      }
      mq.addEventListener('change', handler)
    }
  }

  return { theme, isDark, setTheme, init, syncFromDb }
})
